import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

const useActiveSection = () => {
  const [active, setActive] = useState("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    NAV_LINKS.forEach(({ target }) => {
      const el = document.getElementById(target);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return active;
};

export const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (target) => {
    setOpen(false);
    window.setTimeout(() => scrollToSection(target), open ? 220 : 0);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-border/80 bg-navy-950/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="shell flex h-20 items-center justify-between gap-6 lg:h-24">
          <button
            type="button"
            onClick={() => go("home")}
            aria-label="Montaire Group — back to top"
            data-testid="nav-logo"
            className="shrink-0 transition-opacity duration-300 hover:opacity-80"
          >
            <img
              src={BRAND.wordmark}
              alt="Montaire Group"
              className="h-6 w-auto sm:h-7"
              width="900"
              height="170"
            />
          </button>

          <div className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map(({ label, target }) => (
              <button
                key={target}
                type="button"
                onClick={() => go(target)}
                data-testid={`nav-link-${target}`}
                className={`label-mono relative py-2 transition-colors duration-300 ${
                  active === target
                    ? "text-offwhite"
                    : "text-mist hover:text-offwhite"
                }`}
              >
                {label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-px bg-aqua-bright transition-all duration-500 ${
                    active === target ? "w-full" : "w-0"
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go("contact")}
              data-testid="nav-get-in-touch"
              className="label-mono hidden border border-aqua/60 bg-aqua/10 px-6 py-3 text-offwhite transition-all duration-300 hover:border-aqua-bright hover:bg-aqua/20 hover:shadow-[0_0_24px_-12px_hsl(var(--aqua-bright))] sm:block"
            >
              Get in touch
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-mobile-open"
              className="border border-border p-3 text-offwhite transition-colors duration-300 hover:border-aqua/60 lg:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="nav-mobile-panel"
            className="fixed inset-0 z-[60] bg-navy-950/97 backdrop-blur-xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="shell flex h-20 items-center justify-between">
              <img
                src={BRAND.wordmark}
                alt="Montaire Group"
                className="h-6 w-auto"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-mobile-close"
                className="border border-border p-3 text-offwhite"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="shell mt-10 flex flex-col">
              {NAV_LINKS.map(({ label, target }, i) => (
                <motion.button
                  key={target}
                  type="button"
                  onClick={() => go(target)}
                  data-testid={`nav-mobile-link-${target}`}
                  className="display flex items-baseline gap-4 border-b border-border/70 py-5 text-left text-2xl text-offwhite"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.08, duration: 0.4 }}
                >
                  <span className="font-mono text-[10px] tracking-[0.2em] text-aqua">
                    0{i + 1}
                  </span>
                  {label}
                </motion.button>
              ))}
              <motion.button
                type="button"
                onClick={() => go("contact")}
                data-testid="nav-mobile-get-in-touch"
                className="label-mono mt-10 border border-aqua/60 bg-aqua/10 px-6 py-4 text-offwhite"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.4 }}
              >
                Get in touch
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
